import React, { useCallback, useRef, useState } from 'react';

import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import type { FulfillmentCarrier, GoodsIssueDetail } from '../types';
import { ReasonModal } from './dialogs';

export interface UndoShippingTarget {
  /** The shipped outbound order that goes back to Ready to pick up. */
  orderCode: string;
  goodsIssueID: string;
  /** A TRANSFER order: its destination PO is cancelled with it. */
  transfer: boolean;
  /** Who carries the goods; a Dash shipment is cancelled, a manual carrier has none. */
  carrier?: FulfillmentCarrier | null;
  /**
   * The goods issue version the page loaded (ISO): a row that changed since
   * answers 409 GOODS_ISSUE_STALE instead of being undone blindly.
   */
  expectedUpdatedAt?: string;
}

/**
 * "Batal submit shipping" — the inverse of Submit shipping while the goods are
 * still at the hub: the order is back at Ready to pick up (carrier / AWB
 * cleared, a Dash shipment cancelled, stock reserved again, a TRANSFER order's
 * destination PO cancelled). Shared by Shipping detail and Outbound request
 * detail: `request(target)` opens the reason dialog, render `dialog` once.
 *
 * `onChanged` runs after a change: with the goods issue the server answered
 * on success, with nothing when the document moved meanwhile (reload it).
 * Any other refusal (rider already took it, the destination already received,
 * wrong status) is something the user can act on and stays in the dialog.
 */
export function useUndoShipping(
  onChanged: (detail?: GoodsIssueDetail) => void,
  testId = 'undo-ship-dialog'
) {
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const [target, setTarget] = useState<UndoShippingTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  const request = useCallback((t: UndoShippingTarget) => {
    setError(null);
    setTarget(t);
  }, []);

  const confirm = async (reason: string) => {
    if (!target || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await warehouseApi.undoShipping(
        target.goodsIssueID,
        reason,
        target.expectedUpdatedAt
      );
      toast.success('Submit shipping dibatalkan', {
        description: `${target.orderCode} · kembali ke Ready to pick up.`,
      });
      setTarget(null);
      onChangedRef.current(res?.detail);
    } catch (err) {
      const e = parseWarehouseError(err);
      if (e.status === 409 && e.code === 'GOODS_ISSUE_STALE') {
        setTarget(null);
        toast.error('Shipping ini sudah berubah', { description: e.message });
        onChangedRef.current();
      } else {
        setError(
          e.fieldErrors.find((f) => f.field === 'reason')?.message ?? e.message
        );
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const manualCarrier = !!target?.carrier && target.carrier !== 'DASH';
  const dialog = (
    <ReasonModal
      open={!!target}
      title="Batal submit shipping"
      subtitle={target?.orderCode}
      notice={[
        'Barang belum keluar hub:',
        manualCarrier ? 'order kembali' : 'shipment dibatalkan, order kembali',
        'ke Ready to pick up dan stok di-reserve lagi. Waybill lama tidak berlaku.',
        target?.transfer ? 'PO transfer di hub tujuan ikut dibatalkan.' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      confirmLabel="Batal submit shipping"
      placeholder="Mis. salah pilih kurir atau barang belum jadi dikirim"
      busy={busy}
      error={error}
      testId={testId}
      onConfirm={(reason) => void confirm(reason)}
      onClose={() => setTarget(null)}
    />
  );

  return { request, dialog };
}
